import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Popper, Fade, Paper, useTheme, IconButton, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';

export interface PackageAiValidationAlertProps {
  validationState?: {
    loading?: boolean;
    valid?: boolean | null;
    suggestions?: string[];
    error?: string | null;
  };
}

export default function PackageAiValidationAlert({ validationState }: PackageAiValidationAlertProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isBadgeHovered, setIsBadgeHovered] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const lastStateRef = useRef<string>('');

  const loading = validationState?.loading;
  const valid = validationState?.valid;
  const suggestions = validationState?.suggestions || [];

  // Reset dismissal whenever validationState updates to a new result
  useEffect(() => {
    const currentStateSig = JSON.stringify({
      loading: validationState?.loading,
      valid: validationState?.valid,
      suggestions: validationState?.suggestions,
      error: validationState?.error,
    });

    if (currentStateSig !== lastStateRef.current) {
      lastStateRef.current = currentStateSig;
      setIsDismissed(false);
    }
  }, [validationState?.loading, validationState?.valid, validationState?.suggestions, validationState?.error]);

  // If valid === true or no validation has run yet, show nothing
  if (!loading && (valid === true || valid === null || valid === undefined)) {
    return null;
  }

  const isInvalid = !loading && valid === false;
  const isOpen = isInvalid && (!isDismissed || isBadgeHovered);

  const mainMessage =
    suggestions.length > 1
      ? 'Package details need your attention:'
      : suggestions.length === 1
      ? suggestions[0]
      : validationState?.error || "This package couldn't be verified by AI.";

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', my: 0.5 }}>
      {loading && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            color: theme.palette.primary.main,
            px: 1,
            py: 0.5,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: '0.95rem' }} />
        </Box>
      )}

      {!loading && valid === false && (
        <>
          <Tooltip title={isDismissed ? 'Show AI Alert' : ''} arrow>
            <Box
              ref={setAnchorEl}
              onClick={() => {
                if (isDismissed) setIsDismissed(false);
              }}
              onMouseEnter={() => setIsBadgeHovered(true)}
              onMouseLeave={() => setIsBadgeHovered(false)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                color: theme.palette.mode === 'dark' ? '#F87171' : '#B91C1C',
                px: 1,
                py: 0.35,
                borderRadius: '6px',
                border: '1px solid #F87171',
                background: theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.12)' : '#FEF2F2',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': {
                  background: theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.22)' : '#FEE2E2',
                },
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: '0.85rem', color: '#EF4444' }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                Package Alert{suggestions.length > 1 ? ` (${suggestions.length})` : ''}
              </Typography>
            </Box>
          </Tooltip>

          <Popper
            open={Boolean(isOpen && anchorEl)}
            anchorEl={anchorEl}
            placement="bottom-start"
            transition
            disablePortal={false}
            modifiers={[
              {
                name: 'preventOverflow',
                enabled: true,
                options: { altAxis: true, padding: 12, boundary: 'clippingParents' },
              },
              {
                name: 'flip',
                enabled: true,
                options: { padding: 12 },
              },
              {
                name: 'offset',
                options: { offset: [0, 8] },
              },
            ]}
            style={{ zIndex: 990, maxWidth: 420 }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={isDismissed ? 0 : { enter: 150, exit: 0 }}>
                <Paper
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: '12px',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
                    border: 'none',
                    background: theme.palette.mode === 'dark' ? '#261616' : '#FEF2F2',
                    animation: 'fadeInSlideUp 0.15s ease-out',
                    '@keyframes fadeInSlideUp': {
                      from: {
                        opacity: 0,
                        transform: 'translateY(-4px)',
                      },
                      to: {
                        opacity: 1,
                        transform: 'translateY(0)',
                      },
                    },
                  }}
                >
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    {/* Top-Right Dismiss Button */}
                    <IconButton
                      size="small"
                      aria-label="Dismiss AI validation"
                      onClick={e => {
                        e.stopPropagation();
                        setIsBadgeHovered(false);
                        setIsDismissed(true);
                      }}
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        p: '4px',
                        color: theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280',
                        opacity: 0.7,
                        '&:hover': {
                          opacity: 1,
                          backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: '0.85rem' }} />
                    </IconButton>

                    {/* Content Row: Left Alert Icon + Title & Messages */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, pr: 2.5 }}>
                      <ErrorOutlineIcon
                        sx={{
                          fontSize: '1.15rem',
                          color: '#EF4444',
                          mt: '1px',
                          flexShrink: 0,
                        }}
                      />

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Header with subtle sparkle */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mb: 0.25 }}>
                          <Typography
                            sx={{
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              color: theme.palette.mode === 'dark' ? '#F87171' : '#B91C1C',
                              letterSpacing: '0.1px',
                            }}
                          >
                            Package Alert
                          </Typography>
                          <AutoAwesomeIcon
                            sx={{
                              fontSize: '0.78rem',
                              color: '#EF4444',
                              opacity: 0.9,
                            }}
                          />
                        </Box>

                        {/* Combined Concise Error Text */}
                        <Typography
                          sx={{
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            color: theme.palette.mode === 'dark' ? '#E5E7EB' : '#1F2937',
                            lineHeight: 1.45,
                          }}
                        >
                          {mainMessage}
                        </Typography>

                        {/* Multiple suggestions/bullets if present */}
                        {suggestions.length > 1 && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.75 }}>
                            {suggestions.map((item, idx) => (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                <Typography sx={{ fontSize: '0.75rem', color: '#EF4444', lineHeight: 1.4 }}>
                                  •
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '0.78rem',
                                    color: theme.palette.mode === 'dark' ? '#D1D5DB' : '#4B5563',
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {item}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Fade>
            )}
          </Popper>
        </>
      )}
    </Box>
  );
}
